/**
 * ArtworkPage
 * Detail-Ansicht einer einzelnen Skulptur
 * Unterstützt GLB, PLY (Gaussian Splat) und reine Bild-Formate
 */

import { useParams, useNavigate } from 'react-router-dom'
import { Scene } from '@/components/three/Scene'
import { PLYViewer } from '@/components/three/SculptureModel'
import { InfoPanel } from '@/components/ui/InfoPanel'
import { useArtworks } from '@/hooks/useArtworks'
import { ViewMode } from '@/types/three'
import { getArtworkMediaType } from '@/types/artwork'
import { mediaUrl } from '@/utils/mediaUrl'
import { Seo, buildArtworkLd, buildBreadcrumbLd } from '@/components/Seo'
import { SITE_BASE } from '@/config/site'
import { useT, STRINGS, useFieldLang } from '@/context/LanguageContext'
import './ArtworkPage.css'

export function ArtworkPage() {
  const { category, slug } = useParams<{ category: string; slug: string }>()
  const navigate = useNavigate()
  const { getArtworkByCategoryAndSlug, loading } = useArtworks()
  const t = useT()
  const pick = useFieldLang()
  // The page no longer has detail / closeup modes — the canvas just
  // shows the hero framing and visitors interact with it via the
  // viewer's own drag rotation.
  const viewMode: ViewMode = 'hero'

  const artwork =
    category && slug ? getArtworkByCategoryAndSlug(category, slug) : null

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>{t(STRINGS.common.loading)}</p>
      </div>
    )
  }

  if (!artwork) {
    return (
      <div className="error-screen">
        <p>{t(STRINGS.common.notFound)}</p>
        <button onClick={() => navigate('/galerie')}>
          {t(STRINGS.common.backToGallery)}
        </button>
      </div>
    )
  }

  // Localised title & description — falls back to DE when no EN
  // sidecar exists yet.
  const title = pick(artwork.title, artwork.title_en) || artwork.title
  const description = pick(artwork.description, artwork.description_en) || artwork.description

  // Determine media type
  const mediaType = getArtworkMediaType(artwork)
  const isPLYOnly = mediaType === 'ply'

  const isImageOnly = mediaType === 'image'
  const hasNoMedia = mediaType === 'none'

  const plyUrl = mediaUrl(artwork.files.ply)
  const imageUrl = mediaUrl(artwork.files.image)

  // No-op tap handler kept around because PLYViewer signature still
  // expects one; we deliberately do nothing on tap now.
  const handleClick = () => {}

  // Render content based on media type
  const renderContent = () => {
    if (isImageOnly && imageUrl) {
      const detailAlt = `${title} — Skulptur von Anna Sacher Santana${artwork.material ? `, ${artwork.material}` : ''}${artwork.year ? `, ${artwork.year}` : ''}`
      return (
        <div className="image-only-viewer">
          <img src={imageUrl} alt={detailAlt} />
        </div>
      )
    }

    if (hasNoMedia) {
      return (
        <div className="no-media-placeholder">
          <svg className="no-media-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
          <p className="no-media-title">{title}</p>
          <p className="no-media-hint">Bilddaten in Vorbereitung</p>
        </div>
      )
    }

    if (isPLYOnly && plyUrl) {
      return <PLYViewer url={plyUrl} viewMode={viewMode} onClick={handleClick} plyBBox={artwork.plyBBox} />
    }

    return <Scene artworks={[artwork]} currentIndex={0} viewMode={viewMode} />
  }

  // Every artwork detail page now scrolls so the visitor can reach the
  // description below the hero (used to be true only for image-only
  // works — 3D works kept a fixed-viewport bottom-sheet that hid the
  // text).
  const isFlowPage = true

  // Pick the strongest preview the page can offer for share cards
  const seoImage =
    (artwork.files.glb || artwork.files.ply
      ? `/content/thumbnails/dark/${artwork.slug}.png`
      : null) || artwork.files.image || null

  const artworkUrl = `${SITE_BASE}/werk/${category}/${slug}`
  const jsonLd = [
    buildArtworkLd({
      title: title,
      description: description,
      year: artwork.year,
      material: artwork.material,
      image: seoImage,
      url: artworkUrl,
    }),
    buildBreadcrumbLd([
      { name: 'Galerie', url: `${SITE_BASE}/galerie` },
      { name: artwork.categoryId.replace(/-/g, ' '), url: `${SITE_BASE}/galerie/${category}` },
      { name: title, url: artworkUrl },
    ]),
  ]

  return (
    <article className={`artwork-page ${isFlowPage ? 'flow-page' : ''}`}>
      <Seo
        type="article"
        title={title}
        description={
          (description || '')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 200) ||
          `Skulptur ${title} von Anna Sacher Santana${artwork.material ? ` aus ${artwork.material}` : ''}.`
        }
        image={seoImage}
        jsonLd={jsonLd}
      />
      {/* Back Button */}
      <button className="back-button" onClick={() => navigate(-1)}>
        ← {t(STRINGS.common.back)}
      </button>

      {/* Hero canvas — no title overlay, no tap-for-details hint */}
      <div className={`scene-container ${isImageOnly ? 'image-mode' : ''}`}>
        {renderContent()}
      </div>

      {/* Title strip lives BELOW the canvas so it never sits across the
          sculpture. Artist line removed — visitors already know who the
          artist is from the header logo. */}
      <div className="artwork-caption">
        <h1 className="artwork-caption-title">{title}</h1>
      </div>

      {/* Info Panel always renders the full description inline now */}
      <InfoPanel artwork={artwork} viewMode="detail" />
    </article>
  )
}
